// Orders.jsx — Main coordinator for the Orders page
// Handles: state, API calls, polling, filtering, sorting
// Passes data to OrdersTable for display
// Built with Tailwind CSS

import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';

import { fmtNum } from './calculations';
import OrdersTable, { ORDER_COLUMNS } from './OrdersTable';
import { useFrozenSort } from './useFrozenSort';
import { downloadRowsAsCSV } from './csvExport';
import useColumnLayout from './useColumnLayout';
import { ColumnsButton } from './ColumnsPanel';
import OrderPopup from './OrderPopup';
import OrderTradesPopup from './OrderTradesPopup';
import { getTheme, getDensity } from './theme';

const API_BASE = '/api';

export default function Orders({ demoMode, sessionId, brokerAuthenticated, user }) {
  // ─── Theme ─────────────────────────────────────────────────
  const [theme, setTheme] = useState(() => localStorage.getItem('ord_theme') || 'dark');
  const isDark = theme === 'dark';
  const t = getTheme(isDark);
  // Per-user column layout (order / width / hidden) — persisted server-side. See useColumnLayout.js.
  const cols = useColumnLayout('orders_columns', ORDER_COLUMNS);

  // ─── Data ──────────────────────────────────────────────────
  const [orders, setOrders] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [ticks, setTicks] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ─── Filters (arrays = multi-select, empty = show all) ────
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState([]);
  const [bsFilter, setBsFilter] = useState([]);
  const [statusFilter, setStatusFilter] = useState([]);
  const [expiryFilter, setExpiryFilter] = useState([]);
  const [accountFilter, setAccountFilter] = useState([]);

  // ─── Sort ──────────────────────────────────────────────────
  const [sortCol, setSortCol] = useState('orderTimestamp');
  const [sortDir, setSortDir] = useState('desc');
  // Sort-on-demand: row values update every second but row ORDER stays frozen.
  const [sortVersion, setSortVersion] = useState(0);
  const autoSortTimerRef = useRef(null);

  // ─── Cancel confirmation ───────────────────────────────────
  const [cancelConfirm, setCancelConfirm] = useState(null);
  const [cancelStatus, setCancelStatus] = useState(null); // null | 'cancelling' | 'success' | 'error'
  const [cancelResult, setCancelResult] = useState('');

  // ─── Polling refs ──────────────────────────────────────────
  const ordersInterval = useRef(null);    // kept for manual refresh after cancel/modify
  const eventSourceRef = useRef(null);    // SSE connection for live ticks + order data
  const sseReconnectTimer = useRef(null); // Controlled reconnect (no browser auto-reconnect)
  const sseReconnectCount = useRef(0);

  // ═══════════════════════════════════════════════════════════
  // DATA LOADING
  // ═══════════════════════════════════════════════════════════

  useEffect(() => {
    fetchOrders(); // initial load — SSE orders event will keep it updated
    connectSSE();  // connect once — backend handles token updates automatically
    autoSortTimerRef.current = setInterval(() => setSortVersion(v => v + 1), 60000);

    return () => {
      if (eventSourceRef.current) { eventSourceRef.current.close(); eventSourceRef.current = null; }
      if (autoSortTimerRef.current) clearInterval(autoSortTimerRef.current);
      if (sseReconnectTimer.current) { clearTimeout(sseReconnectTimer.current); sseReconnectTimer.current = null; }
    };
  }, []);

  function connectSSE() {
    // Connect once on mount — no token list needed in URL.
    // Backend sends initial orders + ticks via SSE events.
    // Backend auto-updates token list when orders change (broker poll every 5s).

    // Close existing connection if any
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    if (sseReconnectTimer.current) {
      clearTimeout(sseReconnectTimer.current);
      sseReconnectTimer.current = null;
    }

    const es = new EventSource(`${API_BASE}/orders/sse?sid=${sessionId || ''}`);
    eventSourceRef.current = es;

    // Snapshot — initial tick data for all order tokens
    es.addEventListener('snapshot', (e) => {
      try {
        const data = JSON.parse(e.data);
        setTicks(data.ticks || {});
      } catch (err) { console.error('[Orders SSE] Snapshot parse error:', err); }
    });

    // Ticks — only changed ticks, pushed every 1 second
    es.addEventListener('ticks', (e) => {
      try {
        const data = JSON.parse(e.data);
        const changed = data.ticks || [];
        if (changed.length === 0) return;
        setTicks(prev => {
          const next = { ...prev };
          changed.forEach(t => {
            next[t.token] = { ...(next[t.token] || {}), ltp: t.ltp, volume: t.volume, oi: t.oi,
              depth: { buy: [{ price: t.bestBuyPrice, quantity: t.bestBuyQty }],
                       sell: [{ price: t.bestSellPrice, quantity: t.bestSellQty }] } };
          });
          return next;
        });
      } catch (err) { console.error('[Orders SSE] Ticks parse error:', err); }
    });

    // Orders — pushed by backend when broker data changes (every 5s check, only on change)
    es.addEventListener('orders', (e) => {
      try {
        const data = JSON.parse(e.data);
        setOrders(data.orders || []);
        setAccounts(data.accounts || []);
        setLoading(false);
        setError(null);
        sseReconnectCount.current = 0; // Connected successfully
      } catch (err) { console.error('[Orders SSE] Orders parse error:', err); }
    });

    es.onerror = () => {
      console.warn('[Orders SSE] Connection error');
      es.close();
      eventSourceRef.current = null;
      const maxRetries = 15;
      if (sseReconnectCount.current < maxRetries) {
        const delay = Math.min(3000 * Math.pow(2, sseReconnectCount.current), 30000);
        sseReconnectCount.current++;
        sseReconnectTimer.current = setTimeout(() => { sseReconnectTimer.current = null; connectSSE(); }, delay);
      }
    };
  }

  async function fetchOrders() {
    try {
      const res = await axios.get(`${API_BASE}/orders`);
      setOrders(res.data.orders || []);
      setAccounts(res.data.accounts || []);
      setLoading(false);
      setError(null);
    } catch (err) {
      console.error('[Orders] Failed to fetch:', err.message);
      if (orders.length === 0) {
        setError('Could not load orders. Is the backend running and are you logged in?');
      }
      setLoading(false);
    }
  }


  // ═══════════════════════════════════════════════════════════
  // CANCEL ORDER
  // ═══════════════════════════════════════════════════════════

  function handleCancelClick(order) {
    setCancelConfirm(order);
    setCancelStatus(null);
    setCancelResult('');
  }

  async function executeCancel() {
    if (!cancelConfirm) return;
    setCancelStatus('cancelling');
    try {
      await axios.post(`${API_BASE}/orders/cancel`, {
        accountId: cancelConfirm.account,
        orderId: cancelConfirm.orderId,
        variety: cancelConfirm.variety || 'regular'
      });
      setCancelStatus('success');
      setCancelResult(`Order ${cancelConfirm.orderId} cancelled`);
      // Refresh orders immediately
      fetchOrders();
      // Auto-close after 2 seconds
      setTimeout(() => { setCancelConfirm(null); setCancelStatus(null); }, 2000);
    } catch (err) {
      setCancelStatus('error');
      setCancelResult(err.response?.data?.error || err.message || 'Cancel failed');
    }
  }

  // ═══════════════════════════════════════════════════════════
  // MODIFY ORDER — opens OrderPopup in modify mode
  // ═══════════════════════════════════════════════════════════

  const [modifyOrder, setModifyOrder] = useState(null);
  const [modifyLots, setModifyLots] = useState(1);
  const [modifyPrice, setModifyPrice] = useState('');
  const [modifyMarginData, setModifyMarginData] = useState(null);

  function handleModifyClick(order) {
    const lotSize = order.lotSize || 1;
    const lotsCount = lotSize > 0 ? Math.round(order.quantity / lotSize) : order.quantity;
    setModifyLots(lotsCount);
    setModifyPrice(String(order.price || ''));
    setModifyMarginData(null);
    setModifyOrder(order);
    // Fetch margin for the current order
    if (order.transactionType === 'SELL') {
      fetchModifyMargin(order.account, order, order.quantity);
    }
  }

  async function fetchModifyMargin(accountId, order, quantity) {
    try {
      const res = await axios.post(`${API_BASE}/margins/order-popup`, {
        accountId: accountId,
        tradingsymbol: order.tradingsymbol,
        exchange: order.exchange || 'NFO',
        transaction_type: 'SELL',
        quantity: quantity
      });
      setModifyMarginData(res.data);
    } catch (err) {
      console.error('[Orders] Modify margin fetch failed:', err.message);
    }
  }

  async function refetchModifyMargin(lots, price) {
    if (!modifyOrder) return;
    setModifyMarginData(null);
    const qty = (lots || 1) * (modifyOrder.lotSize || 1);
    fetchModifyMargin(modifyOrder.account, modifyOrder, qty);
  }

  // ═══════════════════════════════════════════════════════════
  // TRADES POPUP — show fill details for an order
  // ═══════════════════════════════════════════════════════════

  const [tradesPopup, setTradesPopup] = useState(null); // the order to show trades for
  const [tradesData, setTradesData] = useState([]);
  const [tradesLoading, setTradesLoading] = useState(false);

  async function handleTradesClick(order) {
    setTradesPopup(order);
    setTradesData([]);
    setTradesLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/orders/${order.orderId}/trades?accountId=${order.account}`);
      setTradesData(res.data.trades || []);
    } catch (err) {
      console.error('[Orders] Failed to fetch trades:', err.message);
    }
    setTradesLoading(false);
  }

  // ═══════════════════════════════════════════════════════════
  // COMPUTE ROWS + FILTER + SORT
  // ═══════════════════════════════════════════════════════════

  const uniqueExpiries = useMemo(() => ['All', ...new Set(orders.map(o => o.expiryStr).filter(Boolean))].sort((a, b) => a === 'All' ? -1 : b === 'All' ? 1 : a.localeCompare(b)), [orders]);
  const uniqueStatuses = useMemo(() => ['All', ...new Set(orders.map(o => o.status).filter(Boolean))].sort((a, b) => a === 'All' ? -1 : b === 'All' ? 1 : a.localeCompare(b)), [orders]);
  const uniqueAccountLabels = useMemo(() => ['All', ...accounts.map(a => a.label)], [accounts]);
  const accountLabelToId = useMemo(() => {
    const map = {};
    accounts.forEach(a => { map[a.label] = a.id; });
    return map;
  }, [accounts]);

  // STEP 1: FILTER + ENRICH — runs every time ticks/orders/filters change.
  // Values update live but row ORDER stays frozen.
  const filteredRows = useMemo(() => {
    // Enrich orders with live tick data
    let computed = orders.map(ord => {
      const optionTick = ticks[ord.instrumentToken] || {};
      const underlyingTick = ord.underlyingToken ? (ticks[ord.underlyingToken] || {}) : {};
      const depth = optionTick.depth || null;
      const bestBuy = depth?.buy?.[0] || null;
      const bestSell = depth?.sell?.[0] || null;

      const rawVolume = optionTick.volume || 0;
      const rawOI = optionTick.oi || 0;
      const lotSz = ord.lotSize || 1;
      return {
        ...ord,
        ltp: optionTick.ltp || 0,
        spot: underlyingTick.ltp || 0,
        volume: (lotSz > 0) ? Math.round(rawVolume / lotSz) : rawVolume,
        oi: (lotSz > 0) ? Math.round(rawOI / lotSz) : rawOI,
        bestBuyPrice: bestBuy?.price || 0,
        bestBuyQty: bestBuy?.quantity || 0,
        bestSellPrice: bestSell?.price || 0,
        bestSellQty: bestSell?.quantity || 0,
        depth,
      };
    });

    // Filter
    if (search.trim()) {
      const terms = search.toLowerCase().split(/\s+/);
      computed = computed.filter(row => {
        const haystack = `${row.symbol} ${row.underlying} ${row.type} ${row.strike} ${row.expiryStr} ${row.orderId} ${row.accountLabel}`.toLowerCase();
        return terms.every(term => haystack.includes(term));
      });
    }
    if (typeFilter.length > 0) computed = computed.filter(r => typeFilter.includes(r.type));
    if (bsFilter.length > 0) computed = computed.filter(r => bsFilter.includes(r.transactionType));
    if (statusFilter.length > 0) computed = computed.filter(r => statusFilter.includes(r.status));
    if (expiryFilter.length > 0) computed = computed.filter(r => expiryFilter.includes(r.expiryStr));
    if (accountFilter.length > 0) {
      const selectedIds = accountFilter.map(label => accountLabelToId[label] || label);
      computed = computed.filter(r => selectedIds.includes(r.account));
    }

    return computed;
  }, [orders, ticks, search, typeFilter, bsFilter, statusFilter, expiryFilter, accountFilter, accountLabelToId]);

  // STEP 2: SORT — frozen order (shared useFrozenSort). Order changes only on a sort change, the
  // 60s timer / manual Re-sort (sortVersion), or a filter/SET change; per-second updates refresh
  // values in place so rows hold position for the full 60s.
  const membershipKey = `${search}|${typeFilter}|${bsFilter}|${statusFilter}|${expiryFilter}|${accountFilter}`;
  const rows = useFrozenSort(
    filteredRows, sortCol, sortDir, sortVersion, membershipKey,
    (r) => r.orderId
  );

  // ═══════════════════════════════════════════════════════════
  // SUMMARY
  // ═══════════════════════════════════════════════════════════

  const summary = useMemo(() => {
    let total = rows.length;
    let complete = 0, open = 0, rejected = 0, cancelled = 0;
    rows.forEach(r => {
      if (r.status === 'COMPLETE') complete++;
      else if (r.status === 'OPEN' || r.status === 'PENDING' || r.status === 'PARTIAL') open++;
      else if (r.status === 'REJECTED') rejected++;
      else if (r.status === 'CANCELLED') cancelled++;
    });
    return { total, complete, open, rejected, cancelled };
  }, [rows]);

  // ═══════════════════════════════════════════════════════════
  // HANDLERS
  // ═══════════════════════════════════════════════════════════

  function handleSort(col) {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('desc'); }
    setSortVersion(v => v + 1);
    if (autoSortTimerRef.current) clearInterval(autoSortTimerRef.current);
    autoSortTimerRef.current = setInterval(() => setSortVersion(v => v + 1), 60000);
  }

  // ═══════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════

  const inputClass = `px-2 py-1 text-xs rounded border outline-none ${isDark ? 'bg-app-input border-app-border-input text-app-text' : 'bg-gray-100 border-gray-300 text-gray-900'}`;

  // No broker connected — show message
  if (!brokerAuthenticated && !demoMode && orders.length === 0) {
    return (
      <div className={`flex-1 flex items-center justify-center ${isDark ? 'bg-app-bg text-app-text' : 'bg-app-light-bg text-app-text-light'}`}>
        <div className="text-center">
          <div className="text-lg font-bold mb-2">Orders</div>
          <div className="text-sm text-yellow-400 mb-2">No broker account connected</div>
          <div className={`text-xs ${isDark ? 'text-app-text-muted' : 'text-gray-500'}`}>Use the "Broker Login" button in the navbar to connect a Kite account.</div>
        </div>
      </div>
    );
  }

  if (loading && orders.length === 0) {
    return (
      <div className={`flex-1 flex items-center justify-center ${isDark ? 'bg-app-bg text-app-text' : 'bg-app-light-bg text-app-text-light'}`}>
        <div className="text-center">
          <div className="text-lg font-bold mb-2">Orders</div>
          <div className={`text-sm ${isDark ? 'text-app-text-muted' : 'text-gray-500'}`}>Loading orders...</div>
        </div>
      </div>
    );
  }

  if (error && orders.length === 0) {
    const isBrokerError = error.includes('401') || error.includes('authenticated') || error.includes('login');
    return (
      <div className={`flex-1 flex items-center justify-center ${isDark ? 'bg-app-bg text-app-text' : 'bg-app-light-bg text-app-text-light'}`}>
        <div className="text-center">
          <div className="text-lg font-bold mb-2">Orders</div>
          {isBrokerError ? (
            <>
              <div className="text-sm text-yellow-400 mb-2">No broker account connected</div>
              <div className={`text-xs ${isDark ? 'text-app-text-muted' : 'text-gray-500'}`}>Use the "Broker Login" button in the navbar to connect a Kite account.</div>
            </>
          ) : (
            <>
              <div className="text-sm text-accent-red mb-3">{error}</div>
              <button onClick={fetchOrders} className="px-4 py-1.5 text-xs font-semibold bg-accent-blue text-white rounded border-none cursor-pointer">Retry</button>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`flex-1 flex flex-col min-h-0 overflow-hidden font-['Segoe_UI',sans-serif] ${isDark ? 'bg-app-bg text-app-text' : 'bg-app-light-bg text-app-text-light'}`}>

      {/* ─── Top bar ─── */}
      <div className={`flex items-center justify-between px-4 py-2 border-b flex-shrink-0 ${isDark ? 'border-app-border bg-app-header-bg' : 'border-gray-200 bg-gray-50'}`}>
        <div className="flex items-center gap-3">
          <span className="text-[15px] font-bold">Orders</span>
          <span className={`text-xs ${isDark ? 'text-app-text-muted' : 'text-gray-500'}`}>
            {summary.total} order{summary.total !== 1 ? 's' : ''}
          </span>
          {summary.complete > 0 && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-[#1a2e1a] text-accent-green">{summary.complete} filled</span>}
          {summary.open > 0 && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-[#2e2a1a] text-accent-yellow">{summary.open} open</span>}
          {summary.rejected > 0 && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-[#2e1a1a] text-accent-red">{summary.rejected} rejected</span>}
          {demoMode && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-[#2e2a1a] text-accent-yellow">DEMO</span>}
        </div>
        <div className="flex items-center gap-3 text-xs">
          <button onClick={() => setTheme(th => th === 'dark' ? 'light' : 'dark')}
            className={`px-2 py-0.5 text-xs rounded border cursor-pointer ${isDark ? 'bg-app-input border-app-border-input text-app-text-muted' : 'bg-gray-100 border-gray-300 text-gray-500'}`}>
            {isDark ? '☀️' : '🌙'}
          </button>
        </div>
      </div>

      {/* ─── Filter bar ─── */}
      <div className={`flex items-center gap-2 px-4 py-1.5 border-b flex-shrink-0 flex-wrap ${isDark ? 'border-app-border bg-app-header-bg' : 'border-gray-200 bg-gray-50'}`}>
        <input type="text" placeholder="Search: reliance, order id..." value={search} onChange={e => setSearch(e.target.value)}
          className={`w-52 ${inputClass}`} />
        <button onClick={() => { fetchOrders(); setSortVersion(v => v + 1); }} title="Re-sort and refresh orders"
          className="px-2.5 py-0.5 text-[11px] font-semibold rounded border cursor-pointer bg-blue-500/15 text-blue-500 border-blue-500/30">
          ↻ Re-sort
        </button>
        {/* Columns manager — show/hide, drag-to-reorder, reset (also the touch-friendly control). Resize is via header grips. */}
        <ColumnsButton cols={cols} isDark={isDark} />
        <button onClick={() => downloadRowsAsCSV(rows, cols.columns, `orders_${new Date().toISOString().split('T')[0]}.csv`)} title="Download current view as CSV"
          className="px-2.5 py-0.5 text-[11px] font-semibold rounded border cursor-pointer bg-emerald-500/15 text-emerald-600 border-emerald-500/30">
          ↓ CSV
        </button>
        <span className={`text-[11px] ml-auto ${isDark ? 'text-app-text-muted' : 'text-gray-500'}`}>
          {fmtNum(rows.length)} order{rows.length !== 1 ? 's' : ''} · live updates
        </span>
      </div>

      {/* ─── Table ─── */}
      <OrdersTable
        rows={rows}
        sortCol={sortCol} sortDir={sortDir} onSort={handleSort}
        onModifyClick={handleModifyClick}
        onCancelClick={handleCancelClick}
        onTradesClick={handleTradesClick}
        isDark={isDark}
        columns={cols.columns}
        onColumnResize={cols.resize}
        filters={{
          expiryFilter, setExpiryFilter, uniqueExpiries,
          typeFilter, setTypeFilter,
          bsFilter, setBsFilter,
          statusFilter, setStatusFilter, uniqueStatuses,
          accountFilter, setAccountFilter, uniqueAccountLabels,
        }}
        t={t}
      />

      {/* ─── Cancel Confirmation Popup ─── */}
      {cancelConfirm && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/50" onClick={() => { if (!cancelStatus) setCancelConfirm(null); }}>
          <div className={`rounded-lg w-80 shadow-2xl ${isDark ? 'bg-app-card border border-app-border' : 'bg-white border border-gray-200'}`} onClick={e => e.stopPropagation()}>
            <div className={`px-4 py-3 border-b ${isDark ? 'border-app-border' : 'border-gray-200'}`}>
              <div className="font-bold text-sm text-accent-red">Cancel Order</div>
            </div>
            <div className={`px-4 py-3 text-xs space-y-2 ${isDark ? '' : ''}`}>
              <div className="flex justify-between">
                <span className={isDark ? 'text-app-text-muted' : 'text-gray-500'}>Instrument</span>
                <span className="font-semibold">{cancelConfirm.tradingsymbol}</span>
              </div>
              <div className="flex justify-between">
                <span className={isDark ? 'text-app-text-muted' : 'text-gray-500'}>Order ID</span>
                <span className="font-mono text-[10px]">{cancelConfirm.orderId}</span>
              </div>
              <div className="flex justify-between">
                <span className={isDark ? 'text-app-text-muted' : 'text-gray-500'}>Qty</span>
                <span>{fmtNum(cancelConfirm.quantity)}</span>
              </div>
              <div className="flex justify-between">
                <span className={isDark ? 'text-app-text-muted' : 'text-gray-500'}>Account</span>
                <span>{cancelConfirm.accountLabel}</span>
              </div>
            </div>

            {cancelStatus && (
              <div className={`px-4 py-2 text-xs text-center ${cancelStatus === 'success' ? 'text-accent-green' : cancelStatus === 'error' ? 'text-accent-red' : 'text-accent-blue'}`}>
                {cancelStatus === 'cancelling' && 'Cancelling...'}
                {cancelStatus === 'success' && `✅ ${cancelResult}`}
                {cancelStatus === 'error' && `❌ ${cancelResult}`}
              </div>
            )}

            <div className={`px-4 py-3 flex gap-3 border-t ${isDark ? 'border-app-border' : 'border-gray-200'}`}>
              {!cancelStatus || cancelStatus === 'error' ? (
                <>
                  <button onClick={() => setCancelConfirm(null)}
                    className={`flex-1 py-1.5 text-xs font-semibold rounded border cursor-pointer ${isDark ? 'bg-transparent border-app-border-input text-app-text-muted' : 'bg-transparent border-gray-300 text-gray-500'}`}>
                    Keep Order
                  </button>
                  <button onClick={executeCancel}
                    className="flex-1 py-1.5 text-xs font-bold rounded text-white bg-accent-red border-none cursor-pointer">
                    {cancelStatus === 'error' ? 'Try Again' : 'Confirm Cancel'}
                  </button>
                </>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {/* ─── Modify/Cancel Popup (Stage 2) ─── */}
      {modifyOrder && (
        <OrderPopup
          themeCtx={{ t, isDark, rowPad: '6px 8px', fontSize: 12.5 }}
          row={{
            symbol: modifyOrder.symbol || modifyOrder.underlying,
            strike: modifyOrder.strike,
            type: modifyOrder.type,
            expiryStr: modifyOrder.expiryStr,
            tradingsymbol: modifyOrder.tradingsymbol,
            exchange: modifyOrder.exchange || 'NFO',
            ltp: modifyOrder.ltp || 0,
            lotSize: modifyOrder.lotSize || 1,
            depth: modifyOrder.depth || null,
            daysToExpiry: 0,
            margin: 0,
          }}
          orderLots={modifyLots} setOrderLots={setModifyLots}
          orderPrice={modifyPrice} setOrderPrice={setModifyPrice}
          orderMarginData={modifyMarginData}
          orderAccount={modifyOrder.account}
          onAccountChange={() => {}}
          accountMargins={{}}
          margins={{}}
          onRefetchMargin={refetchModifyMargin}
          onClose={() => setModifyOrder(null)}
          mode="modify"
          existingOrder={modifyOrder}
          onOrderChanged={() => { fetchOrders(); setModifyOrder(null); }}
          user={user}
        />
      )}
      {/* ─── Trades Popup (Stage 3) ─── */}
      {tradesPopup && (
        <OrderTradesPopup
          order={tradesPopup}
          trades={tradesData}
          loading={tradesLoading}
          isDark={isDark}
          onClose={() => setTradesPopup(null)}
        />
      )}
    </div>
  );
}
